import React from 'react';
import { useDispatch } from 'react-redux';
import reportsService from '../../../../../../../../../../services/reportsService/reportsService';
import { layoutOperations } from '../../../../../../../../../../ducks/layout';
import { alertsOperations } from '../../../../../../../../../../ducks/alerts';
import { Select } from '../../../../../../../../../common/Kendo/Dropdowns/';
import { Button } from '../../../../../../../../../common/Kendo/Button';
import { StackLayout } from '../../../../../../../../../common/Kendo/Layouts/StackLayout';

/**
 * MisReportToolbar component renders a toolbar for selecting DOT, year, and testing authority
 * and submitting the MIS report request.
 * @param {Object} props - Component properties.
 * @param {Array} props.dotList - List of DOT options.
 * @param {Object} props.selectedDOT - Currently selected DOT option.
 * @param {Function} props.setSelectedDOT - Function to set the selected DOT.
 * @param {Object} props.selectedYear - Currently selected year option.
 * @param {Function} props.setSelectedYear - Function to set the selected year.
 * @param {Array} props.testingAuthorityOptions - List of testing authority options.
 * @param {Object} props.selectedTestingAuthority - Currently selected testing authority option.
 * @param {Function} props.setSelectedTestingAuthority - Function to set the selected testing authority
 * @param {Function} props.onSubmit - Callback function to handle the submission of the MIS report.
 * @returns {JSX.Element} The rendered MisReportToolbar component.
 */
export const MisReportToolbar = ({
  dotList,
  selectedDOT,
  setSelectedDOT,
  selectedYear,
  setSelectedYear,
  testingAuthorityOptions,
  selectedTestingAuthority,
  setSelectedTestingAuthority,
  onSubmit
}) => {
  const dispatch = useDispatch();

  const currentYear = new Date().getFullYear();
  // Only include years from currentYear down to 2019 
  const yearOptions = Array.from({ length: currentYear - 2019 + 1 }, (_, i) => ({
    value: currentYear - i,
    label: (currentYear - i).toString(),
  }));

  const handleSubmit = async () => {
    if (selectedDOT && selectedDOT.value && selectedYear && selectedYear.value && selectedTestingAuthority && selectedTestingAuthority.value) {
      dispatch(layoutOperations.showLoadingPanel());
      try {
        const response = await reportsService.getMisReport({
          dotNumber: selectedDOT.value,
          year: selectedYear.value,
          testingAuthorityId: selectedTestingAuthority.value
        });
        const misReport = response.data;

        onSubmit(misReport);
      } catch (error) {
        console.error('Error fetching MIS Report:', error);
        dispatch(alertsOperations.addAlert('Error fetching MIS Report. Please try again.', 'danger'));
      } finally {
        dispatch(layoutOperations.hideLoadingPanel());
      }
    } else {
      dispatch(alertsOperations.addAlert('Please select DOT Number, Year, and Testing Authority.', 'danger'));
    }
  };

  return (
    <StackLayout
      orientation="horizontal"
      gap={16}
      align={{ vertical: 'bottom' }}
      responsive
    >
      <div style={{ flex: 1 }}>
        <Select
          options={dotList}
          value={selectedDOT}
          onChange={setSelectedDOT}
          label="DOT Number"
          listNoDataRender={() =>
            <div>No DOT numbers available.</div>
          }
        />
      </div>
      <div style={{ flex: 1 }}>
        <Select
          options={yearOptions}
          value={selectedYear}
          onChange={setSelectedYear}
          label="Year"
        />
      </div>
      <div style={{ flex: 1 }}>
        <Select
          options={testingAuthorityOptions}
          value={selectedTestingAuthority}
          onChange={setSelectedTestingAuthority}
          label="Testing Authority"
          listNoDataRender={() =>
            selectedDOT && selectedDOT.value
              ? <div>No testing authorities found</div>
              : <div>Select a DOT number first</div>
          }
        />
      </div>
      <div>
        <Button
          content="Submit"
          onClick={handleSubmit}
          themeColor="info"
        />
      </div>
    </StackLayout>
  );
};